import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getStatementsAndInvoicesLabel } from "../../../billing/utils";
import { useLogo } from "../../../brand/hooks";
import { useHasAnyFeatureEnabled } from "../../../feature/hooks";
import { Feature } from "../../../feature/types";
import { permissionCodes, useHasPermission, useHasPermissions } from "../../../permission/hooks";
import { UICollapse, UIDrawer, UIHidden, UIList, UIListItemButton, UIListItemText } from "../../components";
import {
  AccountBox as AccountBoxIcon,
  ChevronRight as ChevronRightIcon,
  Dashboard as DashboardIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  Inbox as InboxIcon,
  NetworkCheck as NetworkCheckIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
} from "../../components/icons";
import { alpha, createStylesheet, lighten, useTheme } from "../../utils/styles";
import MasqueradingChip from "./MasqueradingChip";
import { UserType } from "../../../user-level/types";
import { useContextProvider } from "../../../context/providers/ContextProvider";

interface MainProps {
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
}

export const drawerWidth = 264;
const billPages = ["/bills", "/usage-analysis", "/service-analysis"];
const billDashPages = ["/bill-dashboard-monthly", "/bill-dashboard-trends"];
const statementPages = ["/statements", "/invoices"];
const accountDetailPages = ["/account"];
const assetsPages = ["/assets"];
const mobilePages = ["/mobile-management", "/bars"];
const activateMobilePages = ["/sim-activate", "/sim-disconnect"];
const paymentPages = ["/payment-type"];
const manageContactsPages = ["/contacts", "/contact/new"];
const manageCostCentresPages = ["/manage-cost-centres", "/update-cost-centres"];
const billLimitAlertsPages = ["/bill-limit-alerts"];
const paymentDetailsPages = ["/payment-type"];
const alertPages = ["/usage-alerts"];
const bundleAlertsPages = ["/bundle-alerts"];
const supportPages = ["/support", "/tickets", "/line-test", "/help", "/logic-monitor"];

function NavMenu({ mobileOpen, handleDrawerToggle }: MainProps) {
  const classes = useStyles({ drawerWidth });
  const location = useLocation();
  const theme = useTheme();
  const logo = useLogo();
  const navigate = useNavigate();
  const { userType } = useContextProvider();

  // Feature Check
  const hasActivateSimFeature = useHasAnyFeatureEnabled(Feature.SimActivations);
  const hasAssetSearchFeature = useHasAnyFeatureEnabled(Feature.AssetSearch);
  const hasBillingDashboardFeature = useHasAnyFeatureEnabled(Feature.BillingDashboard);
  const hasBillLimitAlertsFeature = useHasAnyFeatureEnabled(Feature.BillLimitAlerts);
  const hasBundleAlertsFeature = useHasAnyFeatureEnabled(Feature.BundleAlerts);
  const hasCommunicationsFeature = useHasAnyFeatureEnabled(Feature.Communications);
  const hasContactsFeature = useHasAnyFeatureEnabled(Feature.Contacts);
  const hasCostCentreManagementFeature = useHasAnyFeatureEnabled(Feature.CostCentreManagement);
  const hasDclSupportFeature = useHasAnyFeatureEnabled(Feature.DclSupport);
  const hasDirectDebitFeature = useHasAnyFeatureEnabled(Feature.DirectDebitPayments);
  const hasInvoicesFeature = useHasAnyFeatureEnabled(Feature.Invoices);
  const hasMobileDisconnectsFeature = useHasAnyFeatureEnabled(Feature.MobileDisconnections);
  const hasMobileManagementFeature = useHasAnyFeatureEnabled(
    Feature.AutoSwitchPacStac,
    Feature.Bars,
    Feature.BillLimit,
    Feature.SimLock,
    Feature.SimOrder,
    Feature.SimSwap,
    Feature.SimUnlock,
    Feature.WorldwideDataRoaming,
  );
  const hasStatementsFeature = useHasAnyFeatureEnabled(Feature.Statements);
  const hasUsageAlertsFeature = useHasAnyFeatureEnabled(Feature.UsageAlerts);

  // Permissions Check
  const hasCommunicationsPermission = useHasPermission(permissionCodes.COMMUNICATIONS);
  const hasUserManagePermission = useHasPermission(permissionCodes.USERS_MANAGE);
  const hasBillingManagePermission = useHasPermission(permissionCodes.BILLING_MANAGE);
  const hasAccountManagePermission = useHasPermissions([permissionCodes.ACCOUNT_MANAGE, permissionCodes.ALERTS_ACCESS_FEATURE]);
  const hasContactsViewPermission = useHasPermissions([
    permissionCodes.ACCOUNT_VIEW_CONTACTS,
    permissionCodes.ALERTS_ACCESS_FEATURE,
  ]);
  const hasPaymentsPermission = useHasPermission(permissionCodes.ACCOUNT_PAYMENTS);
  const hasMobileManagementPermission = useHasPermission(permissionCodes.MOBILE_MANAGEMENT);
  const hasAssetsPermission = useHasPermission(permissionCodes.VIEW_ASSETS);
  const hasAlertsPermission = useHasPermission(permissionCodes.ALERTS_ACCESS_FEATURE);
  const hasCostCentreManagementPermission = useHasPermission(permissionCodes.COST_CENTRE_MANAGE);
  const hasSupportPermission = useHasPermission(permissionCodes.SUPPORT_ACCESS);
  const hasLogicMonitorPermission = useHasPermission(permissionCodes.SUPPORT_LOGIC_MONITOR);

  const hasStatementsPermission = Boolean(useHasPermission(permissionCodes.BILLING_STATEMENT));
  const hasInvoicesPermission = Boolean(useHasPermission(permissionCodes.BILLING_STATEMENT));

  const isAccountLevel = userType == UserType.ACCOUNT;

  const canView: Function = (type: string) => {
    switch (type) {
      case "home":
        return true;
      case "billing":
        return canView("bill-dashboard") || canView("bill-explorer") || canView("statements-invoices");
      case "bill-dashboard":
        return hasBillingDashboardFeature;
      case "bill-explorer":
        return hasBillingManagePermission;
      case "statements-invoices":
        return (hasStatementsFeature && hasStatementsPermission) || (hasInvoicesFeature && hasInvoicesPermission);
      case "asset-management":
        return (
          canView("search-assets") ||
          canView("mobile-management") ||
          canView("activate-disconnect-mobile") ||
          canView("manage-cost-centres")
        );
      case "search-assets":
        return hasAssetSearchFeature && hasAssetsPermission;
      case "mobile-management":
        return hasMobileManagementFeature && hasMobileManagementPermission;
      case "activate-disconnect-mobile":
        return (hasActivateSimFeature || hasMobileDisconnectsFeature) && hasMobileManagementPermission && isAccountLevel;
      case "manage-cost-centres":
        return hasCostCentreManagementFeature && hasCostCentreManagementPermission;
      case "bill-limit-alerts":
        return hasBillLimitAlertsFeature && hasMobileManagementPermission;
      case "manage-account":
        return canView("contacts") || canView("account-details") || canView("payments");
      case "contacts":
        return hasContactsFeature && hasContactsViewPermission;
      case "account-details":
        return hasAccountManagePermission;
      case "payments":
        return hasPaymentsPermission && hasDirectDebitFeature;
      case "alerts":
        return canView("usage-alerts") || canView("bundle-alerts") || (canView("bill-limit-alerts") && isAccountLevel);
      case "usage-alerts":
        return hasUsageAlertsFeature && hasAlertsPermission;
      case "bundle-alerts":
        return hasBundleAlertsFeature && hasAlertsPermission;
      case "support":
        return canView("support-tickets") || canView("line-test") || canView("help") || canView("logic-monitor");
      case "support-tickets":
        return hasDclSupportFeature && hasSupportPermission;
      case "line-test":
        return hasDclSupportFeature && hasSupportPermission;
      case "help":
        return hasDclSupportFeature && hasSupportPermission;
      case "logic-monitor":
        return hasDclSupportFeature && hasLogicMonitorPermission;
      case "manage-users":
        return hasUserManagePermission;
      case "communications":
        return hasCommunicationsFeature && hasCommunicationsPermission;
      default:
        return false;
    }
  };

  const isPageActive = (pages: string[]): boolean => {
    return pages.some((p) => {
      if (p === "/usage") return location.pathname === p;
      else return location.pathname.startsWith(p);
    });
  };

  const [openBilling, setOpenBilling] = useState(isPageActive([...billPages, ...statementPages, ...billDashPages]));
  const [openAccount, setOpenAccount] = useState(
    isPageActive([...accountDetailPages, ...manageContactsPages, ...paymentDetailsPages]),
  );
  const [openAssets, setOpenAssets] = useState(
    isPageActive([...assetsPages, ...mobilePages, ...activateMobilePages, ...manageCostCentresPages]),
  );
  const [openSupport, setOpenSupport] = useState(isPageActive([...supportPages]));
  const [openAlerts, setOpenAlerts] = useState(isPageActive([...alertPages, ...bundleAlertsPages, ...billLimitAlertsPages]));

  // Keep the section holding the active page expanded when navigation happens outside the menu
  // (e.g. dashboard quick links); never force-collapse a section the user opened manually.
  useEffect(() => {
    if (isPageActive([...billPages, ...statementPages, ...billDashPages])) setOpenBilling(true);
    if (isPageActive([...accountDetailPages, ...manageContactsPages, ...paymentDetailsPages])) setOpenAccount(true);
    if (isPageActive([...assetsPages, ...mobilePages, ...activateMobilePages, ...manageCostCentresPages])) setOpenAssets(true);
    if (isPageActive(supportPages)) setOpenSupport(true);
    if (isPageActive([...alertPages, ...bundleAlertsPages, ...billLimitAlertsPages])) setOpenAlerts(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const getClassNames = (pages: string[]) => `${classes.listItemNested} ${isPageActive(pages) ? classes.itemActive : ""}`;
  const getTopLevelClassNames = (isActive: boolean) => `${classes.listItem} ${isActive ? classes.itemActive : ""}`;

  const drawer = (
    <div data-cy="nav-drawer" role="presentation" className={classes.listWrap}>
      <div className={classes.logo}>
        <div
          data-cy="nav-drawer-logo"
          style={{
            width: "200px",
            height: "110px",
            background: `url('${logo}') center / contain no-repeat`,
          }}
        />
      </div>
      <MasqueradingChip />
      <UIList disablePadding={false}>
        {canView("home") && (
          <UIListItemButton
            className={getTopLevelClassNames(location.pathname === "/")}
            data-cy="nav-drawer-item-home"
            onClick={() => navigate("/")}
          >
            <DashboardIcon className={classes.icon} />
            <UIListItemText primary="Home" classes={{ primary: classes.itemText }} />
          </UIListItemButton>
        )}
        {canView("billing") && (
          <>
            <UIListItemButton
              className={`${classes.listItem} ${classes.listItemWithTopBorder}`}
              onClick={() => setOpenBilling(!openBilling)}
              data-cy="nav-drawer-item-billing"
            >
              <DescriptionIcon className={classes.icon} />
              <UIListItemText primary="Billing" classes={{ primary: classes.itemText }} />
              {openBilling ? <ExpandMoreIcon className={classes.moreIcon} /> : <ChevronRightIcon className={classes.moreIcon} />}
            </UIListItemButton>
            <UICollapse in={openBilling} timeout="auto" unmountOnExit>
              <UIList component="div" disablePadding>
                {canView("bill-dashboard") && (
                  <UIListItemButton
                    className={getClassNames(billDashPages)}
                    onClick={() => navigate("/bill-dashboard-monthly")}
                    data-cy="nav-drawer-item-bill-dashboard"
                  >
                    <UIListItemText primary="Billing Dashboard" classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
                {canView("bill-explorer") && (
                  <UIListItemButton
                    className={getClassNames(billPages)}
                    onClick={() => navigate("/bills")}
                    data-cy="nav-drawer-item-bills"
                  >
                    <UIListItemText primary="Bill Explorer" classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
                {canView("statements-invoices") && (
                  <UIListItemButton
                    className={getClassNames(statementPages)}
                    onClick={() =>
                      navigate(
                        hasStatementsFeature && hasStatementsPermission
                          ? "/statements"
                          : hasInvoicesFeature && hasInvoicesPermission
                            ? "/invoices"
                            : "",
                      )
                    }
                    data-cy="nav-drawer-item-statements"
                  >
                    <UIListItemText
                      primary={getStatementsAndInvoicesLabel(
                        hasStatementsFeature && hasStatementsPermission,
                        hasInvoicesFeature && hasInvoicesPermission,
                      )}
                      classes={{ primary: classes.itemTextNested }}
                    />
                  </UIListItemButton>
                )}
              </UIList>
            </UICollapse>
          </>
        )}
        {canView("asset-management") && (
          <>
            <UIListItemButton
              className={`${classes.listItem} ${classes.listItemWithTopBorder}`}
              onClick={() => setOpenAssets(!openAssets)}
              data-cy="nav-drawer-item-asset-management"
            >
              <NetworkCheckIcon className={classes.icon} />
              <UIListItemText primary={"Asset Management"} classes={{ primary: classes.itemText }} />
              {openAssets ? <ExpandMoreIcon className={classes.moreIcon} /> : <ChevronRightIcon className={classes.moreIcon} />}
            </UIListItemButton>
            <UICollapse in={openAssets} timeout="auto" unmountOnExit>
              <UIList component="div" disablePadding>
                {canView("search-assets") && (
                  <UIListItemButton
                    className={getClassNames(assetsPages)}
                    onClick={() => navigate("/assets")}
                    data-cy="nav-drawer-item-assets"
                  >
                    <UIListItemText primary="Search Assets" classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
                {canView("mobile-management") && (
                  <UIListItemButton
                    className={getClassNames(mobilePages)}
                    onClick={() => navigate("/mobile-management")}
                    data-cy="nav-drawer-item-manage-mobiles"
                  >
                    <UIListItemText primary="Mobile Management" classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
                {canView("activate-disconnect-mobile") && (
                  <UIListItemButton
                    className={getClassNames(activateMobilePages)}
                    onClick={() =>
                      navigate(hasActivateSimFeature ? "/sim-activate" : hasMobileDisconnectsFeature ? "/sim-disconnect" : "")
                    }
                    data-cy="nav-drawer-item-activate-disconnect-mobiles"
                  >
                    <UIListItemText
                      primary={
                        hasActivateSimFeature && hasMobileDisconnectsFeature
                          ? "Activate/Disconnect Mobiles"
                          : hasActivateSimFeature
                            ? "Activate SIM"
                            : hasMobileDisconnectsFeature
                              ? "Disconnect Mobiles"
                              : ""
                      }
                      classes={{ primary: classes.itemTextNested }}
                    />
                  </UIListItemButton>
                )}
                {canView("manage-cost-centres") && (
                  <>
                    <UIListItemButton
                      className={getClassNames(manageCostCentresPages)}
                      onClick={() => navigate("/manage-cost-centres")}
                      data-cy="nav-drawer-item-manage-cost-centres"
                    >
                      <UIListItemText primary="Manage Cost Centres" classes={{ primary: classes.itemTextNested }} />
                    </UIListItemButton>
                  </>
                )}
              </UIList>
            </UICollapse>
          </>
        )}
        {canView("manage-account") && (
          <>
            <UIListItemButton
              className={`${classes.listItem} ${classes.listItemWithTopBorder}`}
              onClick={() => setOpenAccount(!openAccount)}
              data-cy="nav-drawer-item-account"
            >
              <AccountBoxIcon className={classes.icon} />
              <UIListItemText primary={"Account Management"} classes={{ primary: classes.itemText }} />
              {openAccount ? <ExpandMoreIcon className={classes.moreIcon} /> : <ChevronRightIcon className={classes.moreIcon} />}
            </UIListItemButton>
            <UICollapse in={openAccount} timeout="auto" unmountOnExit>
              <UIList component="div" disablePadding>
                {canView("contacts") && (
                  <UIListItemButton
                    className={getClassNames(manageContactsPages)}
                    onClick={() => navigate("/contacts")}
                    data-cy="nav-drawer-item-manage-contacts"
                  >
                    <UIListItemText primary="Manage Contacts" classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
                {canView("account-details") && (
                  <UIListItemButton
                    className={getClassNames(accountDetailPages)}
                    onClick={() => navigate("/account")}
                    data-cy="nav-drawer-item-account-details"
                  >
                    <UIListItemText primary="Account Details" classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
                {canView("payments") && (
                  <UIListItemButton
                    className={getClassNames(paymentPages)}
                    onClick={() => navigate("/payment-type")}
                    data-cy="nav-drawer-item-account-payment-details"
                  >
                    <UIListItemText primary="Payment Details" classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
              </UIList>
            </UICollapse>
          </>
        )}
        {canView("alerts") && (
          <>
            <UIListItemButton
              className={`${classes.listItem} ${classes.listItemWithTopBorder}`}
              onClick={() => setOpenAlerts(!openAlerts)}
              data-cy="nav-drawer-item-alerts"
            >
              <VisibilityIcon className={classes.icon} />
              <UIListItemText primary={"Alerts"} classes={{ primary: classes.itemText }} />
              {openAlerts ? <ExpandMoreIcon className={classes.moreIcon} /> : <ChevronRightIcon className={classes.moreIcon} />}
            </UIListItemButton>
            <UICollapse in={openAlerts} timeout="auto" unmountOnExit>
              <UIList component="div" disablePadding>
                {canView("usage-alerts") && (
                  <UIListItemButton
                    className={getClassNames([alertPages?.[0]])}
                    onClick={() => navigate("/usage-alerts")}
                    data-cy="nav-drawer-item-usage-alerts"
                  >
                    <UIListItemText primary="Usage Alerts" classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
                {canView("bundle-alerts") && (
                  <UIListItemButton
                    className={getClassNames([bundleAlertsPages?.[0]])}
                    onClick={() => navigate("/bundle-alerts")}
                    data-cy="nav-drawer-item-bundle-alerts"
                  >
                    <UIListItemText primary="Bundle Alerts" classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
                {canView("bill-limit-alerts") && (
                  <>
                    <UIListItemButton
                      className={getClassNames(billLimitAlertsPages)}
                      onClick={() => navigate("/bill-limit-alerts-cli")}
                      data-cy="nav-drawer-item-bill-limit-alerts"
                    >
                      <UIListItemText primary="Mobile Bill Limit Alerts" classes={{ primary: classes.itemTextNested }} />
                    </UIListItemButton>
                  </>
                )}
              </UIList>
            </UICollapse>
          </>
        )}
        {canView("support") && (
          <>
            <UIListItemButton
              className={`${classes.listItem} ${classes.listItemWithTopBorder}`}
              onClick={() => setOpenSupport(!openSupport)}
              data-cy="nav-drawer-item-support"
            >
              <InboxIcon className={classes.icon} />
              <UIListItemText primary={"Support"} classes={{ primary: classes.itemText }} />
              {openSupport ? <ExpandMoreIcon className={classes.moreIcon} /> : <ChevronRightIcon className={classes.moreIcon} />}
            </UIListItemButton>
            <UICollapse in={openSupport} timeout="auto" unmountOnExit>
              <UIList component="div" disablePadding>
                {canView("help") && (
                  <UIListItemButton
                    className={getClassNames([supportPages?.[3]])}
                    onClick={() => navigate("/help")}
                    data-cy="nav-drawer-item-help"
                  >
                    <UIListItemText primary={"Help"} classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
                {canView("support-tickets") && (
                  <UIListItemButton
                    className={getClassNames([supportPages?.[1]])}
                    onClick={() => navigate("/tickets")}
                    data-cy="nav-drawer-item-tickets"
                  >
                    <UIListItemText primary={"Tickets"} classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
                {canView("line-test") && (
                  <UIListItemButton
                    className={getClassNames([supportPages?.[2]])}
                    onClick={() => navigate("/line-test")}
                    data-cy="nav-drawer-item-line-test"
                  >
                    <UIListItemText primary={"Line Test"} classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
                {canView("logic-monitor") && (
                  <UIListItemButton
                    className={getClassNames([supportPages?.[4]])}
                    onClick={() => navigate("/logic-monitor")}
                    data-cy="nav-drawer-item-logic-monitor"
                  >
                    <UIListItemText primary={"Monitoring"} classes={{ primary: classes.itemTextNested }} />
                  </UIListItemButton>
                )}
              </UIList>
            </UICollapse>
          </>
        )}
        {canView("communications") && (
          <>
            <UIListItemButton
              className={`${getTopLevelClassNames(isPageActive(["/communications"]))} ${classes.listItemWithTopBorder}`}
              onClick={() => navigate("/communications")}
              data-cy="nav-drawer-item-communications"
            >
              <DescriptionIcon className={classes.icon} />
              <UIListItemText primary="Communications" classes={{ primary: classes.itemText }} />
            </UIListItemButton>
          </>
        )}
        {canView("manage-users") && (
          <>
            <UIListItemButton
              className={`${getTopLevelClassNames(isPageActive(["/users", "/user/", "/roles", "/role/"]))} ${classes.listItemWithTopBorder}`}
              data-cy="nav-drawer-item-users"
              onClick={() => navigate("/users")}
            >
              <SettingsIcon className={classes.icon} />
              <UIListItemText primary="Manage Users" classes={{ primary: classes.itemText }} />
            </UIListItemButton>
          </>
        )}
      </UIList>
    </div>
  );
  return (
    <nav className={classes.drawer}>
      <UIHidden smUp implementation="css">
        <UIDrawer
          variant="temporary"
          anchor={theme.direction === "rtl" ? "right" : "left"}
          open={mobileOpen}
          onClose={handleDrawerToggle}
          classes={{
            paper: classes.drawerPaper,
          }}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {drawer}
        </UIDrawer>
      </UIHidden>
      <UIHidden smDown implementation="css">
        <UIDrawer
          classes={{
            paper: classes.drawerPaper,
          }}
          variant="permanent"
          open
        >
          {drawer}
        </UIDrawer>
      </UIHidden>
    </nav>
  );
}

const useStyles = createStylesheet((theme, props: { drawerWidth: number }) => ({
  listWrap: {
    width: drawerWidth,
    backgroundColor: theme.palette.background.paper,
    height: "100%",
  },
  logo: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
  },
  listItem: {
    padding: `${theme.spacing(1.25)} ${theme.spacing(1.5)}`,
    margin: `2px ${theme.spacing(1.5)}`,
    width: "auto",
    borderRadius: 8,
    color: theme.palette.text.primary,
    cursor: "pointer",
    transition: "background-color .15s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  },
  listItemWithTopBorder: {
    marginTop: theme.spacing(0.5),
  },
  listItemNested: {
    padding: `${theme.spacing(1)} ${theme.spacing(1.5)} ${theme.spacing(1)} ${theme.spacing(6)}`,
    margin: `1px ${theme.spacing(1.5)}`,
    width: "auto",
    borderRadius: 8,
    cursor: "pointer",
    transition: "background-color .15s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  },
  itemText: {
    fontSize: "14px",
    fontWeight: 500,
  },
  itemTextNested: {
    fontSize: "14px",
    color: theme.palette.text.secondary,
  },
  itemActive: {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
    "& .MuiListItemText-primary": {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  icon: {
    marginRight: "20px",
    width: "20px",
    height: "20px",
    color: "inherit",
    opacity: 0.75,
  },
  moreIcon: {
    width: "18px",
    height: "18px",
    color: theme.palette.text.secondary,
  },
  divider: {
    backgroundColor: lighten(theme.palette.tertiary.main, 0.2),
  },
  drawer: {
    [theme.breakpoints.up("sm")]: {
      width: props.drawerWidth,
      flexShrink: 0,
    },
    color: theme.palette.text.primary,
  },
  drawerPaper: {
    width: props.drawerWidth,
    overflowX: "hidden",
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRight: `1px solid ${theme.palette.grey[100]}`,
  },
}));

export default NavMenu;
